/** Real backend client — talks to the endpoints from the API contract. */
import { request, requestMutation, authedFetch } from './http';
import { env } from '@/shared/config/env';
import { tokenStore } from './token-store';
import type { ApiClient, AnalyticsQuery, PaginatedQuery } from './contract';
import {
  BillSchema,
  BillListSchema,
  BillingReportSchema,
  CaptchaSchema,
  CatalogListSchema,
  ClientBalanceSchema,
  ClientCatalogSchema,
  ClientFilterOptionsSchema,
  ClientListSchema,
  ClientSchema,
  ClientUsageSchema,
  ErrorCodesSchema,
  HealthSchema,
  LatencyHistogramSchema,
  LoginResponseSchema,
  LoginStatsSchema,
  ModelDetailSchema,
  ModelsResponseSchema,
  MyCatalogSchema,
  ModelRequestSchema,
  ModelRequestListSchema,
  PricingListSchema,
  PricingRowSchema,
  ProviderSchema,
  ProviderCredentialSchema,
  ProviderListSchema,
  ReadySchema,
  RequestDetailSchema,
  RequestsPageSchema,
  StatusCodesSchema,
  SummarySchema,
  SystemSchema,
  TimeseriesSchema,
  TransactionPageSchema,
} from '@/shared/types/schemas';
import { z } from 'zod';

function base(q: AnalyticsQuery) {
  return { window: q.window, env: q.env };
}
function paginated(q?: PaginatedQuery) {
  return { cursor: q?.cursor, limit: q?.limit };
}

export const realClient: ApiClient = {
  /* ---- auth ---- */
  getCaptcha: (signal) =>
    request({ path: '/captcha', schema: CaptchaSchema, anonymous: true, signal }),
  async login(username, password, captchaId, captchaText) {
    return request({
      method: 'POST',
      path: '/userLogin',
      schema: LoginResponseSchema,
      body: { username, password, captchaId, captchaText },
      anonymous: true,
    });
  },
  async logout() {},

  /* ---- analytics ---- */
  getSummary: (q) => request({ path: '/analytics/summary', schema: SummarySchema, query: base(q), signal: q.signal }),
  getTimeseries: (q) =>
    request({ path: '/analytics/timeseries', schema: TimeseriesSchema, query: { ...base(q), metric: 'hitfail' }, signal: q.signal }),
  getStatusCodes: (q) =>
    request({ path: '/analytics/status-codes', schema: StatusCodesSchema, query: base(q), signal: q.signal }),
  getErrorCodes: (q) =>
    request({ path: '/analytics/error-codes', schema: ErrorCodesSchema, query: base(q), signal: q.signal }),
  getLatencyHistogram: (q) =>
    request({ path: '/analytics/latency-histogram', schema: LatencyHistogramSchema, query: base(q), signal: q.signal }),
  getModels: (q) =>
    request({ path: '/analytics/models', schema: ModelsResponseSchema, query: base(q), signal: q.signal }),
  getModelDetail: (modelId, q) =>
    request({ path: `/analytics/models/${encodeURIComponent(modelId)}`, schema: ModelDetailSchema, query: base(q), signal: q.signal }),
  getRequests: (q) =>
    request({
      path: '/requests',
      schema: RequestsPageSchema,
      query: { ...base(q), model: q.filters.model, status: q.filters.status, errorCode: q.filters.errorCode, q: q.filters.q, cursor: q.cursor, limit: q.limit },
      signal: q.signal,
    }),
  getRequestDetail: (requestId, signal) =>
    request({ path: `/requests/${encodeURIComponent(requestId)}`, schema: RequestDetailSchema, signal }),
  getHealth: (signal) => request({ path: '/health', schema: HealthSchema, anonymous: true, signal }),
  getReady: (signal) => request({ path: '/ready', schema: ReadySchema, anonymous: true, signal }),
  getSystem: (q) => request({ path: '/analytics/system', schema: SystemSchema, query: base(q), signal: q.signal }),
  getLoginStats: (q) =>
    request({ path: '/analytics/logins', schema: LoginStatsSchema, query: { window: q.window }, signal: q.signal }),

  /* ---- admin: client management ---- */
  getClients: (signal) =>
    request({ path: '/admin/clients', schema: ClientListSchema, adminToken: true, signal }),
  getClient: (id, signal) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}`, schema: ClientSchema, adminToken: true, signal }),
  createClient: (input) =>
    requestMutation({ method: 'POST', path: '/admin/clients', schema: ClientSchema, body: input, adminToken: true }),
  updateClient: (id, patch) =>
    requestMutation({ method: 'PUT', path: `/admin/clients/${encodeURIComponent(id)}`, schema: ClientSchema, body: patch, adminToken: true }),
  deleteClient: (id) =>
    requestMutation({ method: 'DELETE', path: `/admin/clients/${encodeURIComponent(id)}`, schema: z.object({ deleted: z.boolean() }), adminToken: true }).then(() => undefined),
  toggleClient: (id, active) =>
    requestMutation({ method: 'PATCH', path: `/admin/clients/${encodeURIComponent(id)}/toggle`, schema: z.object({ active: z.boolean() }), body: { active }, adminToken: true }),
  rechargeClient: (id, amount, note) =>
    requestMutation({ method: 'POST', path: `/admin/clients/${encodeURIComponent(id)}/recharge`, schema: z.object({ newBalance: z.number(), credited: z.number() }), body: { amount, note }, adminToken: true }),
  regenerateApiKey: (id) =>
    requestMutation({ method: 'POST', path: `/admin/clients/${encodeURIComponent(id)}/regenerate-key`, schema: z.object({ apiKey: z.string() }), adminToken: true }),
  revealApiKey: (id) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/reveal-key`, schema: z.object({ apiKey: z.string().nullable() }), adminToken: true }),
  resetClientPassword: (id, password) =>
    requestMutation({ method: 'POST', path: `/admin/clients/${encodeURIComponent(id)}/reset-password`, schema: z.object({ password: z.string() }), body: password ? { password } : {}, adminToken: true }),
  getClientTransactions: (id, q) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/transactions`, schema: TransactionPageSchema, query: { ...paginated(q), year: q?.year, month: q?.month, type: q?.type }, adminToken: true, signal: q?.signal }),
  getClientRequests: (id, q) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/requests`, schema: RequestsPageSchema, query: { ...paginated(q), year: q?.year, month: q?.month, model: q?.model, outcome: q?.outcome }, adminToken: true, signal: q?.signal }),
  getClientUsage: (id, signal) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/usage`, schema: ClientUsageSchema, adminToken: true, signal }),
  getClientFilterOptions: (id, signal) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/filters`, schema: ClientFilterOptionsSchema, adminToken: true, signal }),
  getModelCatalog: (signal) =>
    request({ path: '/admin/models', schema: CatalogListSchema, adminToken: true, signal }),
  getClientCatalog: (id, signal) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/catalog`, schema: ClientCatalogSchema, adminToken: true, signal }),
  setClientPrice: (id, modelKey, price) =>
    requestMutation({ method: 'PUT', path: `/admin/clients/${encodeURIComponent(id)}/pricing/${encodeURIComponent(modelKey)}`, schema: z.object({ modelKey: z.string(), price: z.number() }), body: { price }, adminToken: true }).then(() => undefined),
  clearClientPrice: (id, modelKey) =>
    requestMutation({ method: 'DELETE', path: `/admin/clients/${encodeURIComponent(id)}/pricing/${encodeURIComponent(modelKey)}`, schema: z.object({ cleared: z.boolean() }), adminToken: true }).then(() => undefined),
  getClientBilling: (id, q) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/billing`, schema: BillingReportSchema, query: { year: q.year, month: q.month, model: q.model }, adminToken: true, signal: q.signal }),
  async downloadClientBillingCsv(id, q) {
    const params = new URLSearchParams({ year: String(q.year), month: String(q.month), format: 'csv' });
    if (q.model) params.set('model', q.model);
    const url = `${env.VITE_API_BASE_URL}/admin/clients/${encodeURIComponent(id)}/billing?${params.toString()}`;
    const token = tokenStore.getAccess();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Bill download failed (${res.status}).`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = q.filename ?? `bill-${q.year}-${String(q.month).padStart(2, '0')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },

  /* ---- admin: bills ---- */
  uploadClientBill: async (id, file, label) => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    const token = tokenStore.getAccess();
    const url = `${env.VITE_API_BASE_URL}/admin/clients/${encodeURIComponent(id)}/bills`;
    const res = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Upload failed');
    return { bill: BillSchema.parse(json.data.bill) };
  },
  getClientBills: (id, signal) =>
    request({ path: `/admin/clients/${encodeURIComponent(id)}/bills`, schema: BillListSchema, adminToken: true, signal }),
  deleteClientBill: (id, billId) =>
    requestMutation({ method: 'DELETE', path: `/admin/clients/${encodeURIComponent(id)}/bills/${encodeURIComponent(billId)}`, schema: z.object({ deleted: z.boolean() }), adminToken: true }).then(() => undefined),
  downloadAdminBill: async (billId, filename) => {
    const url = `${env.VITE_API_BASE_URL}/admin/bills/${encodeURIComponent(billId)}/download`;
    const token = tokenStore.getAccess();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Bill download failed (${res.status}).`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },

  /* ---- admin: pricing ---- */
  getPricing: (signal) =>
    request({ path: '/admin/pricing', schema: PricingListSchema, adminToken: true, signal }),
  setPricing: (modelKey, cost) =>
    requestMutation({ method: 'PUT', path: `/admin/pricing/${encodeURIComponent(modelKey)}`, schema: PricingRowSchema, body: { cost }, adminToken: true }),
  setModelConfig: (modelKey, patch) =>
    requestMutation({ method: 'PUT', path: `/admin/model-config/${encodeURIComponent(modelKey)}`, schema: z.object({}).passthrough(), body: patch, adminToken: true }).then(() => undefined),

  /* ---- admin: provider credentials ---- */
  getProviders: (signal) =>
    request({ path: '/admin/providers', schema: ProviderListSchema, adminToken: true, signal }),
  createProvider: (name, label) =>
    requestMutation({ method: 'POST', path: '/admin/providers', schema: z.object({ provider: ProviderSchema }), body: { name, label }, adminToken: true }).then((r) => r.provider),
  toggleProvider: (id, active) =>
    requestMutation({ method: 'PATCH', path: `/admin/providers/${encodeURIComponent(id)}/toggle`, schema: z.object({ active: z.boolean() }), body: { active }, adminToken: true }),
  deleteProvider: (id) =>
    requestMutation({ method: 'DELETE', path: `/admin/providers/${encodeURIComponent(id)}`, schema: z.object({ deleted: z.boolean() }), adminToken: true }).then(() => undefined),
  addCredential: (providerId, key, value) =>
    requestMutation({ method: 'POST', path: `/admin/providers/${encodeURIComponent(providerId)}/credentials`, schema: z.object({ credential: ProviderCredentialSchema }), body: { key, value }, adminToken: true }).then((r) => r.credential),
  updateCredential: (id, value) =>
    requestMutation({ method: 'PUT', path: `/admin/credentials/${encodeURIComponent(id)}`, schema: z.object({ updated: z.boolean() }), body: { value }, adminToken: true }).then(() => undefined),
  deleteCredential: (id) =>
    requestMutation({ method: 'DELETE', path: `/admin/credentials/${encodeURIComponent(id)}`, schema: z.object({ deleted: z.boolean() }), adminToken: true }).then(() => undefined),
  revealCredential: (id) =>
    request({ path: `/admin/credentials/${encodeURIComponent(id)}/reveal`, schema: z.object({ value: z.string().nullable() }), adminToken: true }),

  /* ---- admin: model catalogue management ---- */
  createModel: (input) =>
    requestMutation({ method: 'POST', path: '/admin/models', schema: z.object({ created: z.boolean() }), body: input, adminToken: true }).then(() => undefined),
  updateModel: (key, patch) =>
    requestMutation({ method: 'PUT', path: `/admin/models/${encodeURIComponent(key)}`, schema: z.object({ updated: z.boolean() }), body: patch, adminToken: true }).then(() => undefined),
  toggleModelActive: (key, active) =>
    requestMutation({ method: 'PATCH', path: `/admin/models/${encodeURIComponent(key)}/active`, schema: z.object({ active: z.boolean() }), body: { active }, adminToken: true }).then(() => undefined),

  /* ---- admin: model-access requests ---- */
  getModelRequests: (status, signal) =>
    request({ path: '/admin/model-requests', schema: ModelRequestListSchema, query: status ? { status } : undefined, adminToken: true, signal }),
  setModelRequestStatus: (id, status, price) =>
    requestMutation({ method: 'PATCH', path: `/admin/model-requests/${encodeURIComponent(id)}`, schema: z.object({ status: z.string() }), body: { status, price }, adminToken: true }).then(() => undefined),

  /* ---- client portal ---- */
  getMyProfile: (signal) => request({ path: '/client/me', schema: ClientSchema, signal }),
  revealMyApiKey: () => request({ path: '/client/reveal-key', schema: z.object({ apiKey: z.string().nullable() }) }),
  getMyCatalog: (signal) => request({ path: '/client/catalog', schema: MyCatalogSchema, signal }),
  getMyUsage: (signal) => request({ path: '/client/usage', schema: ClientUsageSchema, signal }),
  getMyModelRequests: (signal) =>
    request({ path: '/client/model-requests', schema: z.object({ requests: z.array(ModelRequestSchema) }), signal }),
  createMyModelRequest: (modelKey, note) =>
    requestMutation({ method: 'POST', path: '/client/model-requests', schema: z.object({ requested: z.boolean() }), body: { modelKey, note } }).then(() => undefined),
  tryDocument: async (docType, files) => {
    const formData = new FormData();
    for (const file of files) formData.append('file', file);
    // authedFetch (not raw fetch): refreshes the access token on a 401 and retries.
    const res = await authedFetch(`/client/try/${encodeURIComponent(docType)}`, {
      method: 'POST',
      body: formData,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.success) {
      // Non-JSON bodies (413 from the proxy, 502/504) carry no message — surface
      // the status so the failure is diagnosable instead of a blank "try again".
      if (json?.message) throw new Error(json.message);
      if (res.status === 413) throw new Error('Those files are too large to upload. Try fewer or smaller files.');
      if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
      throw new Error(`Could not process the document (HTTP ${res.status}).`);
    }
    return { results: json.data.results };
  },
  getMyBalance: (signal) => request({ path: '/client/balance', schema: ClientBalanceSchema, signal }),
  getMyRequests: (q) =>
    request({ path: '/client/requests', schema: RequestsPageSchema, query: paginated(q), signal: q?.signal }),
  getMyTransactions: (q) =>
    request({ path: '/client/transactions', schema: TransactionPageSchema, query: paginated(q), signal: q?.signal }),
  changeMyPassword: (currentPassword, newPassword) =>
    requestMutation({
      method: 'POST',
      path: '/client/change-password',
      schema: z.object({ changed: z.boolean() }),
      body: { currentPassword, newPassword },
    }).then(() => undefined),
  getMyBills: (signal) =>
    request({ path: '/client/bills', schema: BillListSchema, signal }),
  downloadMyBill: async (billId, filename) => {
    const url = `${env.VITE_API_BASE_URL}/client/bills/${encodeURIComponent(billId)}/download`;
    const token = tokenStore.getAccess();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Bill download failed (${res.status}).`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },
  viewMyBill: async (billId) => {
    // Open the tab synchronously (inside the click gesture) so pop-up blockers
    // allow it, then point it at the fetched blob once auth'd bytes arrive.
    const win = window.open('', '_blank');
    try {
      const url = `${env.VITE_API_BASE_URL}/client/bills/${encodeURIComponent(billId)}/download`;
      const token = tokenStore.getAccess();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Bill view failed (${res.status}).`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      if (win) win.location.href = href;
      else window.open(href, '_blank');
      // Keep the object URL alive long enough for the new tab to load it.
      setTimeout(() => URL.revokeObjectURL(href), 60_000);
    } catch (err) {
      win?.close();
      throw err;
    }
  },
};
